import { api } from '@/api';
import {
  DevicePresenceStateDto,
  IotDeviceStateOfUse,
  QaEquipmentSubCategory,
} from '@/api/generator';
import { connectedArrayInfoOf } from '@/components/deviceStatus/utils/deviceInformation';
import { isIOTDevice } from '@/utils/rtSystem';
import { waitForConnectionState } from '@/utils/signalR';
import { HubConnection, HubConnectionState } from '@microsoft/signalr';
import { Ref, computed, ref, shallowRef } from 'vue';
import {
  DeviceStatusIslandBattery,
  DeviceStatusIslandConnectedArray,
  DeviceStatusIslandItem,
  DeviceStatusIslandState,
} from './deviceStatusIslandTypes';

const DEVICE_STATUS_EVENT = 'DeviceStatusUpdated';

const STATE_MAP: Partial<Record<IotDeviceStateOfUse, DeviceStatusIslandState>> = {
  [IotDeviceStateOfUse.Online]: 'online',
  [IotDeviceStateOfUse.Initializing]: 'initializing',
  [IotDeviceStateOfUse.Measuring]: 'measuring',
  [IotDeviceStateOfUse.DeviceError]: 'error',
};

interface LiveDevice {
  id: string;
  kind: 'unicheck' | 'detectorInterface';
  name: string;
  serialNumber?: string | null;
  typeNumber?: string | null;
  pictureId?: string | null;
  presence: Ref<DevicePresenceStateDto | undefined>;
}

// shallowRef on purpose: the per-device presence refs are tracked explicitly
// via .value reads in the computed below and must not be auto-unwrapped.
const devices = shallowRef<LiveDevice[]>([]);
/** deviceId → the subscription token to release via UnsubscribeDevice. */
const subscriptionTokens = new Map<string, string>();
/** Gates rendering: false while the connection is down, so the island hides
 * instead of showing unvouched last-known states. */
const connected = ref(false);
let active = false;
let hub: HubConnection | undefined;
// Bumped by stopLive()/restart() so a superseded in-flight connect() or
// subscribe discards its result instead of resurrecting what was torn down.
let generation = 0;
const hookedConnections = new WeakSet<HubConnection>();

function batteryOf(presence: DevicePresenceStateDto): DeviceStatusIslandBattery | undefined {
  const capacity = presence.batteryInformation?.currentCapacityPercent;
  if (typeof capacity !== 'number') {
    return undefined;
  }
  return { percent: capacity < 0 ? 'poweradapter' : capacity };
}

function connectedArraysOf(presence: DevicePresenceStateDto): DeviceStatusIslandConnectedArray[] {
  const array = connectedArrayInfoOf(presence.deviceInformation);
  return array ? [array] : [];
}

export const liveItems = computed<DeviceStatusIslandItem[]>(() => {
  if (!connected.value) {
    return [];
  }
  return devices.value.flatMap<DeviceStatusIslandItem>((device) => {
    const presence = device.presence.value;
    const state = presence?.state ? STATE_MAP[presence.state] : undefined;
    if (!presence || !state) {
      return [];
    }
    if (device.kind === 'detectorInterface') {
      const connectedArrays = connectedArraysOf(presence);
      return [
        {
          id: device.id,
          type: 'detectorInterface' as const,
          state: state === 'online' && connectedArrays.length > 0 ? 'connected' : state,
          headline: device.name,
          content: {
            serialNumber: device.serialNumber,
            typeNumber: device.typeNumber,
            pictureId: device.pictureId,
            connectedArrays,
          },
        },
      ];
    }
    return [
      {
        id: device.id,
        type: 'unicheck' as const,
        state,
        headline: device.name,
        battery: batteryOf(presence),
        content: {
          serialNumber: device.serialNumber,
          typeNumber: device.typeNumber,
          pictureId: device.pictureId,
        },
      },
    ];
  });
});

export const hasActiveDevices = computed(() => liveItems.value.length > 0);

let pushCount = 0;

function onDeviceStatusUpdated(value: DevicePresenceStateDto) {
  const device = value.id
    ? devices.value.find((candidate) => candidate.id === value.id)
    : undefined;
  if (device) {
    device.presence.value = value;
  }
  if (import.meta.env.DEV) {
    console.debug(
      `[DeviceStatusIsland] push #${++pushCount}: id=${value.id} state=${value.state} ` +
        `array=${value.deviceInformation?.arrayDetector ?? '-'} ` +
        `${device ? 'in island' : 'not an island device'}`,
    );
  }
}

async function subscribeDevice(connection: HubConnection, deviceId: string, gen: number) {
  try {
    const token = (await connection.invoke<string>('SubscribeDevice', deviceId)) ?? deviceId;
    if (gen !== generation || !active) {
      // Superseded while in flight — release the server-side subscription.
      void connection.invoke('UnsubscribeDevice', token).catch(() => undefined);
      return;
    }
    subscriptionTokens.set(deviceId, token);
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn(`[DeviceStatusIsland] SubscribeDevice(${deviceId}) failed:`, error);
    }
  }
}

function unsubscribeAll() {
  const connection = hub;
  if (connection && connection.state === HubConnectionState.Connected) {
    subscriptionTokens.forEach((token) => {
      void connection.invoke('UnsubscribeDevice', token).catch(() => undefined);
    });
  }
  subscriptionTokens.clear();
}

function installHooks(connection: HubConnection) {
  if (hookedConnections.has(connection)) {
    return;
  }
  hookedConnections.add(connection);

  connection.onreconnecting(() => {
    if (active) {
      subscriptionTokens.clear();
      connected.value = false;
    }
  });
  connection.onreconnected(() => {
    if (active) {
      const gen = generation;
      connected.value = true;

      for (const device of devices.value) {
        void subscribeDevice(connection, device.id, gen);
      }
    }
  });
  connection.onclose(() => {
    if (active) {
      subscriptionTokens.clear();
      connected.value = false;
    }
  });
}

function restart() {
  unsubscribeAll();
  devices.value = [];
  connected.value = false;
  generation += 1;
  void connect(generation);
}

async function connect(gen: number) {
  const connection = hub;
  if (!connection) {
    return;
  }
  try {
    const { result } = await api.qaEquipmentApi.getQaEquipmentRtSystemList({});
    const found: LiveDevice[] = (result?.value ?? []).flatMap<LiveDevice>((device) => {
      if (!device.id || !isIOTDevice(device) || device.state !== 'Enabled') {
        return [];
      }
      return [
        {
          id: device.id,
          kind:
            device.subCategory === QaEquipmentSubCategory.Interface
              ? ('detectorInterface' as const)
              : ('unicheck' as const),
          name: device.name ?? '',
          serialNumber: device.serialNumber,
          typeNumber: device.typeNumber,
          pictureId: device.imageId || device.defaultImageId,
          presence: ref<DevicePresenceStateDto | undefined>(),
        },
      ];
    });

    if (connection.state === HubConnectionState.Disconnected) {
      await connection.start();
    }
    // A cold hub (negotiate + transport upgrade) can take a few seconds; the
    // helper's default ~1s budget gives up too early and rejects.
    await waitForConnectionState(connection, HubConnectionState.Connected, 200, 50);

    // stopLive()/restart() may have superseded this attempt while fetching.
    if (gen !== generation || !active) {
      return;
    }

    devices.value = found;
    connected.value = true;
    for (const device of found) {
      void subscribeDevice(connection, device.id, gen);
    }
  } catch (error) {
    // Fire-and-forget like useDeviceStatusSignalR: no retry timer. The island
    // stays hidden until the next refreshLive or a reload.
    if (import.meta.env.DEV) {
      console.warn('[DeviceStatusIsland] live mode failed to start:', error);
    }
  }
}

/**
 * Enters live mode (or restarts it if already live). Always a full restart,
 * so a re-entry after a failed start retries immediately.
 */
export function startLive(connection: HubConnection) {
  if (hub && hub !== connection) {
    hub.off(DEVICE_STATUS_EVENT, onDeviceStatusUpdated);
  }
  hub = connection;
  // off before on: a re-entry must not stack a second handler.
  connection.off(DEVICE_STATUS_EVENT, onDeviceStatusUpdated);
  connection.on(DEVICE_STATUS_EVENT, onDeviceStatusUpdated);
  installHooks(connection);
  active = true;
  restart();
}

/**
 * Re-runs the equipment fetch and the per-device subscriptions; no-op unless
 * live mode is active. Needed because both are a startup snapshot: the hub
 * only publishes devices somebody subscribed to, so equipment whose cloud
 * registration changed after start would stay invisible (or stale) until the
 * next startLive. The Host calls this when a register/deregister completes.
 */
export function refreshLive() {
  if (active) {
    restart();
  }
}

/** Leaves live mode: mutes the connection hooks and releases everything. */
export function stopLive() {
  active = false;
  generation += 1;
  hub?.off(DEVICE_STATUS_EVENT, onDeviceStatusUpdated);
  unsubscribeAll();
  devices.value = [];
  connected.value = false;
}
